import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  Button,
  Grid,
  InputLabel,
  Chip,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { useState } from "react";
import InfoIcon from "@mui/icons-material/Info";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Swal from 'sweetalert2';

const FaleConosco = () => {
  const theme = useTheme();
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    cpf: "",
    nascimento: "",
    sexo: "",
    grauInstrucao: "",
    email: "",
    tipoTelefone: "",
    contato: "",
    setorDestino: "",
    formaContato: "",
    natureza: "",
    mensagem: "",
  });
  const [attachments, setAttachments] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      setAttachments([...e.target.files]);
    }
  };

  const handleRemoveFile = (file) => {
    setAttachments((prevFiles) => prevFiles.filter((f) => f !== file));
  };

  const handleSubmit = async () => {
    // Validação de campos obrigatórios
    let formIsValid = true;
    const newFormData = { ...formData };

    if (!isAnonymous) {
      // Validação dos campos pessoais
      const personalFields = [
        "nome",
        "cpf",
        "nascimento",
        "sexo",
        "grauInstrucao",
        "email",
        "tipoTelefone",
        "contato",
      ];
      personalFields.forEach((field) => {
        if (!newFormData[field]) {
          formIsValid = false;
          // Adicionar uma indicação de erro ou validação para o campo
        }
      });
    }

    // Verificação dos campos obrigatórios de "Informações da manifestação"
    if (!newFormData.setorDestino || !newFormData.mensagem) {
      formIsValid = false;
      // Adicionar uma indicação de erro ou validação para o campo
    }

    if (!formIsValid) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos obrigatórios',
        text: 'Preencha todos os campos obrigatórios.',
        confirmButtonText: 'OK',
        confirmButtonColor: theme.palette.primary.main,
      });
      return; // Evitar o envio do formulário se inválido
    }

    // Mostrar loading durante o envio
    Swal.fire({
      title: 'Enviando manifestação...',
      text: 'Por favor, aguarde.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    // Envio do formulário
    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (
        !isAnonymous ||
        (isAnonymous &&
          ![
            "nome",
            "cpf",
            "nascimento",
            "sexo",
            "grauInstrucao",
            "email",
            "tipoTelefone",
            "contato",
          ].includes(key))
      ) {
        data.append(key, String(value));
      }
    });

    attachments.forEach((file) => {
      data.append("files", file);
    });

    try {
      const response = await fetch("https://seu-backend.com/api/enviar-email", {
        method: "POST",
        body: data,
      });

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: 'Manifestação enviada com sucesso!',
          confirmButtonText: 'OK',
          confirmButtonColor: theme.palette.primary.main,
        }).then(() => {
          // Limpar formulário após sucesso
          setFormData({
            nome: "",
            cpf: "",
            nascimento: "",
            sexo: "",
            grauInstrucao: "",
            email: "",
            tipoTelefone: "",
            contato: "",
            setorDestino: "",
            formaContato: "",
            natureza: "",
            mensagem: "",
          });
          setAttachments([]);
          setIsAnonymous(false);
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro!',
          text: 'Erro ao enviar manifestação.',
          confirmButtonText: 'Tentar novamente',
          confirmButtonColor: theme.palette.primary.main,
        });
      }
    } catch (error) {
      console.error("Erro ao enviar formulário:", error);
      Swal.fire({
        icon: 'error',
        title: 'Erro de conexão!',
        text: 'Erro ao enviar manifestação. Verifique sua conexão e tente novamente.',
        confirmButtonText: 'OK',
        confirmButtonColor: theme.palette.primary.main,
      });
    }
  };

  return (
    <Box sx={{ padding: 4, maxWidth: 1400, margin: "0 auto" }}>
      {/* Título Principal */}
      <Box
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: "#fff",
          padding: 3,
          borderRadius: 2,
          marginBottom: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: "bold", marginBottom: 1 }}>
          CENTRAL DE OUVIDORIA
        </Typography>
        <Typography variant="subtitle1">
          Câmara Municipal de Serra Caiada
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* PRIMEIRA COLUNA - FORMULÁRIO (Mais importante) */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: "fit-content" }}>
            <CardContent sx={{ padding: 4 }}>
              <Box
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                  padding: 2,
                  borderRadius: 1,
                  marginBottom: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <InfoIcon />
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Formulário para a manifestação
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{ marginBottom: 3, color: theme.palette.text.secondary }}
              >
                Acesse nosso canal de ouvidoria e envie suas manifestações,
                críticas, elogios, denúncias e ajude o governo a tornar nossa
                cidade cada vez melhor.
              </Typography>

              {/* Manifestação anônima */}
              <Box sx={{ marginBottom: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                    />
                  }
                  label="Sim, é anônima."
                  sx={{ color: theme.palette.text.primary }}
                />
              </Box>

              {/* Informações pessoais */}
              {!isAnonymous && (
                <>
                  <Typography
                    variant="subtitle1"
                    sx={{ marginBottom: 2, fontWeight: "bold" }}
                  >
                    Informações pessoais
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Nome"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        required={!isAnonymous}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="CPF"
                        name="cpf"
                        value={formData.cpf}
                        onChange={handleChange}
                        required={!isAnonymous}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Data de nascimento"
                        type="date"
                        name="nascimento"
                        value={formData.nascimento}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        required={!isAnonymous}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Sexo</InputLabel>
                        <Select
                          name="sexo"
                          value={formData.sexo}
                          onChange={handleChange}
                          required={!isAnonymous}
                        >
                          <MenuItem value="masculino">Masculino</MenuItem>
                          <MenuItem value="feminino">Feminino</MenuItem>
                          <MenuItem value="outro">Outro</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Grau de instrução</InputLabel>
                        <Select
                          name="grauInstrucao"
                          value={formData.grauInstrucao}
                          onChange={handleChange}
                        >
                          <MenuItem value="fundamental">Fundamental</MenuItem>
                          <MenuItem value="médio">Médio</MenuItem>
                          <MenuItem value="superior">Superior</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="E-mail"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required={!isAnonymous}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Tipo de telefone</InputLabel>
                        <Select
                          name="tipoTelefone"
                          value={formData.tipoTelefone}
                          onChange={handleChange}
                        >
                          <MenuItem value="celular">Celular</MenuItem>
                          <MenuItem value="fixo">Fixo</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Contato"
                        name="contato"
                        value={formData.contato}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Informações da manifestação */}
              <Typography
                variant="subtitle1"
                sx={{ marginY: 3, fontWeight: "bold" }}
              >
                Informações da manifestação
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Setor de destino</InputLabel>
                    <Select
                      name="setorDestino"
                      value={formData.setorDestino}
                      onChange={handleChange}
                      required
                    >
                      <MenuItem value="administrativo">Administrativo</MenuItem>
                      <MenuItem value="financeiro">Financeiro</MenuItem>
                      <MenuItem value="gabinete">
                        Gabinete da presidência
                      </MenuItem>
                      <MenuItem value="ouvidoria">Ouvidoria</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Mensagem"
                    name="mensagem"
                    multiline
                    rows={4}
                    value={formData.mensagem}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="outlined" component="label">
                    Escolher arquivos
                    <input
                      type="file"
                      multiple
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                </Grid>
                {/* Exibir arquivos anexados */}
                {attachments.length > 0 && (
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      sx={{ marginTop: 2, fontWeight: "bold" }}
                    >
                      Arquivos anexados:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {Array.from(attachments).map((file, index) => (
                        <Chip
                          key={index}
                          label={file.name}
                          onDelete={() => handleRemoveFile(file)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Botão de envio */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 4,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleSubmit}
                >
                  Enviar manifestação
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* SEGUNDA COLUNA - INFORMAÇÕES DO SIC */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: "fit-content", backgroundColor: "#f8f9fa" }}>
            <CardContent sx={{ padding: 4 }}>
              <Box
                sx={{
                  backgroundColor: "#ffa726",
                  color: "#fff",
                  padding: 2,
                  borderRadius: 1,
                  marginBottom: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <InfoIcon />
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  E-SIC - PEDIDO DE INFORMAÇÃO
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{ marginBottom: 3, lineHeight: 1.6 }}
              >
                SIC (Serviço de Informações ao Cidadão) permite que qualquer
                pessoa encaminhe pedidos de informação aos órgãos e entidades do
                Poder Legislativo Municipal.
              </Typography>

              <Box sx={{ marginBottom: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    marginBottom: 1,
                    color: theme.palette.primary.main,
                  }}
                >
                  Gestor do SIC
                </Typography>
                <Typography variant="body2">
                  Madiane Assunção Paiva da Silva
                </Typography>
              </Box>

              <Divider sx={{ marginY: 2 }} />

              <Box sx={{ marginBottom: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    marginBottom: 1,
                    color: theme.palette.primary.main,
                  }}
                >
                  Unidade/Setor responsável
                </Typography>
                <Typography variant="body2">OUVIDORIA</Typography>
              </Box>

              <Divider sx={{ marginY: 2 }} />

              <Box sx={{ marginBottom: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    marginBottom: 2,
                    color: theme.palette.primary.main,
                  }}
                >
                  Contatos do SIC
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    marginBottom: 1,
                  }}
                >
                  <PhoneIcon
                    sx={{ fontSize: 16, color: theme.palette.text.secondary }}
                  />
                  <Typography variant="body2">(84) 3190-3179</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <EmailIcon
                    sx={{ fontSize: 16, color: theme.palette.text.secondary }}
                  />
                  <Typography variant="body2">
                    ouvidoria@cmserracaiada.rn.gov.br
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ marginY: 2 }} />

              <Box sx={{ marginBottom: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    marginBottom: 1,
                    color: theme.palette.primary.main,
                  }}
                >
                  Endereço do SIC
                </Typography>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <LocationOnIcon
                    sx={{
                      fontSize: 16,
                      color: theme.palette.text.secondary,
                      marginTop: 0.5,
                    }}
                  />
                  <Typography variant="body2">
                    R. Nossa Sra. da Conceição, Nº 334
                    <br />
                    Serra Caiada, RN
                    <br />
                    CEP: 59245-000
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ marginY: 2 }} />

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    marginBottom: 1,
                    color: theme.palette.primary.main,
                  }}
                >
                  Horário do SIC
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AccessTimeIcon
                    sx={{ fontSize: 16, color: theme.palette.text.secondary }}
                  />
                  <Typography variant="body2">
                    De Segunda à Sexta de 07h00 às 13h00
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ marginTop: 3 }}>
                <Button
                  onClick={() =>
                    (window.location.href =
                      "https://esic-cmserracaiada.prosipe.com")
                  }
                  variant="contained"
                  sx={{
                    backgroundColor: "#ffa726",
                    "&:hover": { backgroundColor: "#ff9800" },
                    width: "100%",
                  }}
                >
                  Acessar o E-SIC
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FaleConosco;